import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { v1Client } from '@renderer/chat'
import { useMemo } from 'react'
import { useUser } from '@renderer/hooks/useUser'
import { getProvidersQueryKey } from '@renderer/hooks/integration-query-keys'

export { PROVIDERS_QUERY_KEY, getProvidersQueryKey } from '@renderer/hooks/integration-query-keys'
export const CLAUDE_CODE_CLIENT_CAPABILITY = 'claude-code-targets'

/**
 * Companion entry to the `ashby` OAuth provider (never a card of its own —
 * it carries no `toolset` tag). Its presence means the backend offers the
 * org-API-key upgrade: the Ashby card gains an "Add API key" menu action that
 * opens the standard ApiKeyDialog for this entry, whose submission stores as
 * an `ashby` row alongside the OAuth one. See the entry's comment in the
 * backend providers route.
 */
export const ASHBY_API_KEY_PROVIDER_ID = 'ashby-api-key'

/**
 * Capability slugs this client knows how to satisfy. Providers with
 * `authType: 'client'` declare a `clientCapability` they require — the server
 * leaves the UX entirely to us, so anything we don't recognize here we hide
 * from the integrations list rather than render a broken card.
 */
export const KNOWN_CLIENT_CAPABILITIES = new Set<string>([CLAUDE_CODE_CLIENT_CAPABILITY, ASHBY_API_KEY_PROVIDER_ID])

export interface Provider {
  id: string
  name: string
  iconUrl: string
  tags: Array<string>
  providerUrl: string
  authType: 'oauth' | 'api-key' | 'client'
  /** Required when authType is 'client'; matches a slug in KNOWN_CLIENT_CAPABILITIES. */
  clientCapability?: string
  description: string
  links?: Array<{ label: string; url: string }>
  /** Steps an admin does on the provider before OAuth can succeed. */
  setup?: {
    description: string
    steps: Array<{ text: string; code?: string; link?: { text: string; url: string } }>
  }
}

/**
 * Drop client-auth providers whose capability this build doesn't implement,
 * so they never appear in any picker where a click would produce an unhandled
 * state.
 */
function filterSupportedProviders(providers: Array<Provider> | undefined): Array<Provider> | undefined {
  if (!providers) return providers
  return providers.filter((p) => {
    if (p.authType !== 'client') return true
    return p.clientCapability !== undefined && KNOWN_CLIENT_CAPABILITIES.has(p.clientCapability)
  })
}

export interface ProvidersQueryOptions {
  /**
   * Poll the catalog on this interval while the caller is mounted. The list is
   * flag-gated per user on the backend, so a caller that stays open across a
   * flag change (onboarding) needs to re-ask; everyone else keeps the default
   * long-lived cache. react-query applies refetch options per observer, so this
   * doesn't change fetching for other consumers of the shared query.
   */
  refetchIntervalMs?: number
}

export function useProviders(options?: ProvidersQueryOptions) {
  const { getAuthToken } = useAuth()
  const user = useUser()
  const isPolling = options?.refetchIntervalMs !== undefined

  return useQuery({
    queryKey: getProvidersQueryKey(user?.id),
    queryFn: async () => {
      const response = await v1Client.GET('/connections/providers', {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
        // Declare which client-auth capabilities this build implements so the
        // backend serves capability-gated providers in their current shape
        // (undeclared → it falls back to a legacy shape old builds can
        // render). The cast can go once gen:api-types picks up the param.
        params: { query: { clientCapabilities: [...KNOWN_CLIENT_CAPABILITIES].join(',') } as never },
      })
      if (response.error) throw new Error('Failed to fetch providers')
      return (response.data?.data ?? []) as Array<Provider>
    },
    staleTime: 30 * 60 * 1000, // 30 minutes — providers list rarely changes
    // refetchInterval ignores staleTime, so the cache config above still holds
    // for every non-polling caller.
    refetchInterval: options?.refetchIntervalMs ?? false,
    refetchOnWindowFocus: isPolling,
    refetchOnMount: isPolling,
  })
}

export function useIntegrationProviders() {
  const query = useProviders()
  const data = useMemo(
    () => filterSupportedProviders(query.data?.filter((p) => p.tags.includes('toolset'))),
    [query.data],
  )
  return { ...query, data }
}

export function useOnboardingIntegrationProviders(options?: ProvidersQueryOptions) {
  const query = useProviders(options)
  const data = useMemo(
    () => filterSupportedProviders(query.data?.filter((p) => p.tags.includes('onboarding'))),
    [query.data],
  )
  return { ...query, data }
}
